// Kanemori Sanctuary (M10, x256-335): building kinds and roofs for the three sanctuary scenes
// (common/data/hd-scenes/sanctuary-*.json). The restored half (checkpoint, sanctuary) is crisp and lit; the monastery
// north of the ridge is weathered, broken and mossy. Forest and mountain filler are face-0 "buildings" whose roof is a
// cedar canopy, painted on a world-anchored grid so neighbouring rectangles and scenes join without seams.
// Ground, edges, upright props and lights are in hd-sanctuary-place.ts. Palette: hd-kit ramps of theme.ts only.
// Presentation only: nothing here changes tiles, collision or gameplay points.
import { hash2 } from '../../common/src/util';
import { HT, K, type G, px, speckle, dither, disc, frame, dk, lt, mix, alpha, text, textW } from './hd-kit';
import { registerFront, registerRoof, type LightFn } from './hd-registry';
import { lantern } from './hd-parts';

/** Sanctuary materials (ramps of the palette through hd-kit) */
export const SK = {
  cedar: dk(K.cyan, 0.74), cedarHi: mix(dk(K.cyan, 0.56), K.amber, 0.08), cedarLo: dk(K.cyan, 0.86), cedarDk: dk(K.cyan, 0.92),
  moss: mix(dk(K.cyan, 0.66), K.amber, 0.28), mossHi: mix(dk(K.cyan, 0.5), K.amber, 0.35), mossLo: mix(dk(K.cyan, 0.8), K.amber, 0.2),
  stone: dk(K.slate, 0.12), stoneHi: lt(K.slate, 0.18), stoneLo: dk(K.slate, 0.42), stoneDk: dk(K.slate, 0.62),
  verm: dk(K.pink, 0.1), vermHi: mix(K.pink, K.amber, 0.35), vermLo: dk(K.pink, 0.42),
  gold: K.amber, goldHi: lt(K.amber, 0.4), goldLo: dk(K.amber, 0.35),
  bronze: dk(mix(K.amber, K.slate, 0.45), 0.25), bronzeHi: mix(K.amber, K.slate, 0.3), verdigris: mix(dk(K.cyan, 0.45), K.slate, 0.3),
  ash: mix(K.wood, K.slate, 0.55), ashHi: mix(K.woodHi, K.slate, 0.45), ashLo: mix(K.woodLo, K.slate, 0.4),
  paper: dk(K.white, 0.12), mist: K.white,
};

// The scene origin in tiles, set by hd-scene before a sanctuary scene's buildings are painted, so the canopy grid is
// anchored to the world rather than to each scene (no seam on the scene borders).
let OX = 0, OY = 0;
export function setSanctuaryOrigin(rx: number, ry: number) { OX = rx * HT; OY = ry * HT; }
/** the scene origin in world hd px (what the canopy grid and the boundary wall anchor to) */
export const sanctuaryOrigin = () => ({ ox: OX, oy: OY });

/** The part of a rect that lands on the canvas being painted (pieces are a translated window of the scene); null when
 *  nothing does, or when painting into the light-only dummy context (no canvas). */
export function visible(g: G, x: number, y: number, w: number, h: number) {
  const c = (g as { canvas?: { width: number; height: number } }).canvas; if (!c || typeof g.getTransform !== 'function') return null;
  const t = g.getTransform();
  const x0 = Math.max(x, -t.e), y0 = Math.max(y, -t.f), x1 = Math.min(x + w, -t.e + c.width), y1 = Math.min(y + h, -t.f + c.height);
  return x1 > x0 && y1 > y0 ? { x0, y0, x1, y1 } : null;
}

// ================================================================== forest canopy
type Mood = 'sanct' | 'mona' | 'mount';
/** One cedar crown seen from above: a dark skirt, layered needles lit from the top-left, a pointed tip. */
function crown(g: G, cx: number, cy: number, r: number, mood: Mood, v: number) {
  const mona = mood === 'mona';
  const base = mona ? mix(SK.cedarLo, SK.mossLo, 0.35) : SK.cedarLo, mid = mona ? mix(SK.cedar, SK.moss, 0.3) : SK.cedar;
  const hi = mona ? SK.mossHi : SK.cedarHi;
  disc(g, cx + 3, cy + 5, r, Math.round(r * 0.8), alpha(K.night, 0.45)); // cast shadow, down-right
  disc(g, cx, cy, r, Math.round(r * 0.86), K.ink);
  disc(g, cx, cy, r - 1, Math.round(r * 0.86) - 1, base);
  // three needle tiers, each smaller and lit further up-left
  for (let k = 0; k < 3; k++) {
    const rr = Math.round((r - 2) * (1 - k * 0.27)), ox = -k * 2, oy = -k * 3;
    disc(g, cx + ox, cy + oy, rr, Math.round(rr * 0.8), k === 0 ? mid : mix(mid, hi, k * 0.45));
    for (let a = 0; a < 7; a++) { // ragged needle tufts on the rim
      const an = a * 0.9 + v * 6, tx = Math.round(cx + ox + Math.cos(an) * rr * 0.9), ty = Math.round(cy + oy + Math.sin(an) * rr * 0.72);
      px(g, tx - 1, ty - 1, 3, 2, k === 2 ? hi : base);
    }
  }
  px(g, cx - 5, cy - 7, 2, 2, lt(hi, 0.3)); px(g, cx - 3, cy - 9, 1, 1, lt(hi, 0.5)); // tip highlight
  if (mona && v > 0.55) speckle(g, cx - r, cy - r, r * 2, r * 2, alpha(SK.mossHi, 0.7), 0.05, Math.floor(v * 97)); // hanging moss
}
/** a cedar forest canopy over a rect (scene hd px), on the world grid */
function canopy(g: G, x: number, y: number, w: number, h: number, mood: Mood) {
  const v = visible(g, x, y, w, h); if (!v) return;
  g.save(); g.beginPath(); g.rect(v.x0, v.y0, v.x1 - v.x0, v.y1 - v.y0); g.clip();
  const floor = mood === 'mona' ? mix(SK.cedarDk, SK.mossLo, 0.3) : SK.cedarDk;
  px(g, v.x0, v.y0, v.x1 - v.x0, v.y1 - v.y0, floor);
  speckle(g, v.x0, v.y0, v.x1 - v.x0, v.y1 - v.y0, SK.cedarLo, 0.08, 3, 2);
  const S = 24, gx0 = Math.floor((v.x0 + OX - 30) / S), gx1 = Math.ceil((v.x1 + OX + 30) / S), gy0 = Math.floor((v.y0 + OY - 30) / S), gy1 = Math.ceil((v.y1 + OY + 30) / S);
  for (let j = gy0; j <= gy1; j++) for (let i = gx0; i <= gx1; i++) {
    const a = hash2(i * 13 + 5, j * 7 + 3), b = hash2(j * 11 + 1, i * 17 + 9);
    const wx = i * S + Math.round(a * S * 0.9) + (j & 1) * 10, wy = j * S + Math.round(b * S * 0.7);
    if (mood === 'mount') { // the mountain: rock and scree between stands of cedar
      const n = hash2(Math.floor(i / 4), Math.floor(j / 3)) * 0.6 + hash2(i, j) * 0.4;
      if (n < 0.34) { rock(g, wx - OX, wy - OY, 8 + Math.round(a * 8), a); continue; }
    }
    crown(g, wx - OX, wy - OY, 12 + Math.round(a * 6), mood, b);
  }
  if (mood === 'mount') { // distance: the lower slope fades into the night
    for (let yy = v.y0; yy < v.y1; yy += 4) { const f = Math.min(1, Math.max(0, (yy + OY - 96 * HT) / (48 * HT))); if (f > 0) px(g, v.x0, yy, v.x1 - v.x0, 4, alpha(K.night, 0.12 + f * 0.45)); }
  }
  g.restore();
}
/** a granite outcrop: a lit top face and a shaded flank */
function rock(g: G, cx: number, cy: number, r: number, v: number) {
  disc(g, cx + 3, cy + 4, r, Math.round(r * 0.7), alpha(K.night, 0.4));
  disc(g, cx, cy, r, Math.round(r * 0.72), K.ink); disc(g, cx, cy, r - 1, Math.round(r * 0.72) - 1, SK.stoneLo);
  disc(g, cx - 2, cy - 2, r - 3, Math.round(r * 0.5), mix(SK.stone, SK.stoneLo, v));
  px(g, cx - r + 3, cy - 2, Math.max(2, r - 2), 1, SK.stoneHi);
  if (v > 0.5) speckle(g, cx - r, cy - r, r * 2, r, SK.moss, 0.12, Math.floor(v * 50));
}

// ================================================================== temple roofs
export interface RoofTone { n: string; s: string; w: string; e: string; line: string; ridge: string; ridgeHi: string; end: string }
export const NEW_TILE: RoofTone = { n: dk(K.slate, 0.15), s: lt(K.slate, 0.08), w: lt(K.slate, 0.2), e: dk(K.slate, 0.35), line: dk(K.slate, 0.55), ridge: lt(K.slate, 0.25), ridgeHi: lt(K.slate, 0.45), end: SK.gold };
export const OLD_TILE: RoofTone = { n: mix(dk(K.slate, 0.1), SK.mossLo, 0.3), s: mix(lt(K.slate, 0.05), SK.moss, 0.3), w: mix(lt(K.slate, 0.15), SK.mossHi, 0.3), e: mix(dk(K.slate, 0.4), SK.mossLo, 0.25), line: dk(K.slate, 0.6), ridge: mix(lt(K.slate, 0.1), SK.moss, 0.3), ridgeHi: SK.mossHi, end: SK.ash };

/** A hipped tile roof seen from above (light from the top-left): north, south, west and east slopes meeting a ridge.
 *  ridgeY: ridge height as a fraction of h; inset: how far the hips come in (hd px; w/2 makes a pyramid). Rows are
 *  filled scanline by scanline so every edge stays on the pixel grid. Tile channels run down each slope. */
export function hipRoof(g: G, x: number, y: number, w: number, h: number, ridgeY: number, inset: number, T: RoofTone) {
  const r = y + Math.round(h * ridgeY), a = Math.min(inset, Math.floor(w / 2));
  for (let yy = y; yy < y + h; yy++) {
    const ins = yy < r ? Math.round(a * (yy - y) / Math.max(1, r - y)) : Math.round(a * (y + h - yy) / Math.max(1, y + h - r));
    const mid = yy < r ? T.n : T.s;
    px(g, x, yy, ins, 1, T.w); px(g, x + w - ins, yy, ins, 1, T.e); px(g, x + ins, yy, w - 2 * ins, 1, mid);
    for (let i = x + ins + 3; i < x + w - ins - 1; i += 5) px(g, i, yy, 1, 1, T.line); // channels down the N/S slopes
    if ((yy - y) % 5 === 0) { px(g, x, yy, ins, 1, T.line); px(g, x + w - ins, yy, ins, 1, T.line); } // and across the W/E hips
    if (ins > 0) { px(g, x + ins - 1, yy, 2, 1, T.ridge); px(g, x + w - ins - 1, yy, 2, 1, T.line); } // hip ridges
  }
  // eaves: tile ends along the bottom, dark drip line, outline
  frame(g, x, y, w, h, K.ink, 2);
  for (let i = x + 3; i < x + w - 3; i += 5) { px(g, i, y + h - 5, 4, 3, T.ridge); px(g, i + 1, y + h - 5, 2, 1, T.ridgeHi); }
  px(g, x + 2, y + 2, w - 4, 1, alpha(K.white, 0.12));
  if (w - 2 * a > 4) { // main ridge with its end tiles
    px(g, x + a - 2, r - 4, w - 2 * a + 4, 8, K.ink); px(g, x + a - 1, r - 3, w - 2 * a + 2, 5, T.ridge); px(g, x + a - 1, r - 3, w - 2 * a + 2, 1, T.ridgeHi);
    for (const ex of [x + a - 6, x + w - a - 4]) { px(g, ex, r - 7, 10, 12, K.ink); px(g, ex + 1, r - 6, 8, 10, T.end); px(g, ex + 1, r - 6, 8, 2, lt(T.end, 0.4)); }
  } else { disc(g, x + w / 2, r, 6, 5, K.ink); disc(g, x + w / 2, r, 4, 3, T.end); }
}
/** holes and missing tiles in an old roof: dark gaps with rafters showing, bare boards, moss */
function ruinRoof(g: G, x: number, y: number, w: number, h: number, seed: number, holes: number) {
  speckle(g, x + 4, y + 4, w - 8, h - 8, SK.moss, 0.06, seed, 2);
  speckle(g, x + 4, y + 4, w - 8, h - 8, SK.mossHi, 0.02, seed + 1, 2);
  for (let k = 0; k < holes; k++) {
    const hw = 24 + Math.floor(hash2(k, seed) * 40), hh = 14 + Math.floor(hash2(seed, k) * 24);
    const hx = x + 12 + Math.floor(hash2(k + 3, seed + 1) * (w - hw - 24)), hy = y + 12 + Math.floor(hash2(seed + 2, k + 5) * (h - hh - 24));
    px(g, hx - 3, hy - 3, hw + 6, hh + 6, SK.ashLo); speckle(g, hx - 3, hy - 3, hw + 6, hh + 6, SK.ash, 0.3, k + seed); // bare boards round the hole
    px(g, hx, hy, hw, hh, K.ink); px(g, hx + 2, hy + 2, hw - 4, hh - 4, K.night);
    for (let i = hx + 4; i < hx + hw - 2; i += 9) { px(g, i, hy, 3, hh, SK.ash); px(g, i, hy, 1, hh, SK.ashHi); } // rafters
    px(g, hx, hy + hh, hw, 2, alpha(K.night, 0.5));
  }
}

// ---- roofs
/** the countryside's forest floor (hd-sanctuary-country.ts paints the ground with it) */
export const COUNTRY_FLOOR = { floor: mix(SK.cedarDk, SK.mossLo, 0.3), floorHi: mix(SK.cedarLo, SK.mossLo, 0.5), litter: mix(K.woodLo, SK.mossLo, 0.45) };
// the ground under the countryside's small solid footprints (tea house, onsen hut, vending machines, fox shrine,
// waterfall rock): forest floor; the structure itself is an upright prop placed from the same footprint
registerRoof('countryBase', { parapet: false, paint: (g, x, y, w, h) => {
  const F = COUNTRY_FLOOR; px(g, x, y, w, h, F.floor); speckle(g, x, y, w, h, F.floorHi, 0.12, x + y, 2); speckle(g, x, y, w, h, F.litter, 0.05, x * 3 + y, 2);
} });
registerRoof('sanctCedar', { parapet: false, paint: (g, x, y, w, h) => canopy(g, x, y, w, h, 'sanct') });
registerRoof('monaForest', { parapet: false, paint: (g, x, y, w, h) => canopy(g, x, y, w, h, 'mona') });
registerRoof('sanctMountain', { parapet: false, paint: (g, x, y, w, h) => canopy(g, x, y, w, h, 'mount') });
registerRoof('sanctHallRoof', { parapet: false, paint: (g, x, y, w, h) => {
  canopy(g, x, y, w, h, 'sanct'); // cedars behind the eaves
  hipRoof(g, x + 8, y + 26, w - 16, h - 26, 0.38, Math.round((w - 16) * 0.24), NEW_TILE);
  // gilded ridge crest and a Kanemori sensor mast on the ridge (the corp restored it)
  const r = y + 26 + Math.round((h - 26) * 0.38), cx = x + w / 2;
  for (let i = cx - 60; i <= cx + 60; i += 30) { px(g, i - 2, r - 6, 4, 4, SK.goldLo); px(g, i - 1, r - 6, 2, 1, SK.goldHi); }
  px(g, cx - 1, r - 22, 3, 18, K.ink); px(g, cx, r - 22, 1, 18, K.metalHi); disc(g, cx, r - 24, 3, 3, K.ink); px(g, cx - 1, r - 25, 2, 2, K.cyan);
} });
registerRoof('sanctPagodaRoof', { parapet: false, paint: (g, x, y, w, h) => {
  canopy(g, x, y, w, h, 'sanct');
  const s = Math.min(w - 8, h - 8), px0 = x + (w - s) / 2, py0 = y + h - s - 4;
  hipRoof(g, px0, py0, s, s, 0.5, s / 2, NEW_TILE);
  for (let k = 1; k < 5; k++) { // the five tiers seen from above: each eave ring with its shadow
    const i = Math.round(k * s / 10);
    frame(g, px0 + i, py0 + i, s - 2 * i, s - 2 * i, K.ink, 2);
    px(g, px0 + i + 2, py0 + s - i - 2, s - 2 * i - 4, 2, NEW_TILE.ridgeHi);
    px(g, px0 + i + 2, py0 + i + 2, s - 2 * i - 4, 3, alpha(K.night, 0.35));
    for (let j = px0 + i + 4; j < px0 + s - i - 4; j += 5) px(g, j, py0 + s - i - 5, 3, 2, NEW_TILE.ridge);
  }
  // bells hung at the tier corners
  for (const [cx, cy] of [[px0 + 4, py0 + 4], [px0 + s - 6, py0 + 4], [px0 + 4, py0 + s - 6], [px0 + s - 6, py0 + s - 6]]) { px(g, cx, cy, 3, 4, K.ink); px(g, cx, cy + 1, 3, 2, SK.gold); }
} });
registerRoof('sanctSmallRoof', { parapet: false, paint: (g, x, y, w, h) => {
  canopy(g, x, y, w, h, 'sanct');
  hipRoof(g, x + 4, y + 10, w - 8, h - 10, 0.42, Math.round((w - 8) * 0.3), NEW_TILE);
} });
registerRoof('sanctCorpRoof', { parapet: true, paint: (g, x, y, w, h) => {
  px(g, x, y, w, h, K.concrete); speckle(g, x, y, w, h, K.concreteLo, 0.08, 4);
  px(g, x + 10, y + h - 8, w - 20, 2, K.cyan); px(g, x + 10, y + h - 6, w - 20, 1, alpha(K.cyan, 0.4)); // light strip on the coping
  px(g, x + 24, y + 9, 26, 14, K.ink); px(g, x + 25, y + 10, 24, 12, K.metal); for (let i = 27; i < 48; i += 3) px(g, x + i, y + 11, 1, 10, K.metalLo); // AC
  px(g, x + w - 40, y + 4, 2, 22, K.ink); px(g, x + w - 46, y + 6, 14, 2, K.metalHi); px(g, x + w - 40, y + 3, 2, 2, K.red); // antenna
} });
registerRoof('sanctCorpWall', { parapet: false, paint: (g, x, y, w, h) => {
  canopy(g, x, y, w, h, 'sanct');
  const t = y + 10; // the wall top, then its south face
  px(g, x, t, w, 12, K.ink); px(g, x, t + 1, w, 10, K.concreteHi); px(g, x, t + 1, w, 2, lt(K.concreteHi, 0.2));
  for (let i = x + 4; i < x + w; i += 10) { px(g, i, t - 6, 2, 7, K.ink); px(g, i, t - 6, 1, 6, K.metalHi); } // spikes
  for (let i = x; i < x + w; i += 4) px(g, i, t - 4 + (i % 8 ? 0 : 1), 3, 1, K.metal); // razor wire
  px(g, x, t + 11, w, 30, K.concrete); for (let i = x; i < x + w; i += 40) px(g, i, t + 11, 2, 30, K.concreteLo); // panels
  px(g, x, t + 12, w, 2, K.cyan); px(g, x, t + 14, w, 1, alpha(K.cyan, 0.4));
  const s = 'KANEMORI', tw = textW(s, 2); text(g, s, x + (w - tw) / 2 + 1, t + 21, K.ink, 2); text(g, s, x + (w - tw) / 2, t + 20, lt(K.cyan, 0.3), 2);
  for (let i = x; i < x + w; i += 12) px(g, i, t + 37, 6, 4, (i / 12) % 2 ? K.warm : K.ink); // hazard foot
  px(g, x, t + 41, w, 4, alpha(K.night, 0.5));
} });
registerRoof('sanctTempleWall', { parapet: false, paint: (g, x, y, w, h) => {
  canopy(g, x, y, w, h, 'sanct');
  // tsuiji wall: tile cap seen from above, white plaster face with five gilded lines (a high-ranked temple)
  px(g, x, y, w, 4, K.ink); px(g, x, y + 2, w, 12, NEW_TILE.s); for (let i = x + 2; i < x + w; i += 5) px(g, i, y + 3, 1, 10, NEW_TILE.line);
  px(g, x, y + 2, w, 2, NEW_TILE.ridgeHi); for (let i = x + 1; i < x + w; i += 5) { px(g, i, y + 13, 4, 3, NEW_TILE.ridge); }
  px(g, x, y + 16, w, 2, K.ink); px(g, x, y + 18, w, 3, alpha(K.night, 0.5));
  px(g, x, y + 21, w, 26, SK.paper); speckle(g, x, y + 21, w, 26, K.plaster, 0.05, 7);
  for (let k = 0; k < 5; k++) px(g, x, y + 24 + k * 4, w, 1, SK.goldLo);
  for (let i = x; i < x + w; i += 64) { px(g, i, y + 21, 3, 26, SK.ashLo); }
  px(g, x, y + 47, w, 6, SK.stoneLo); px(g, x, y + 47, w, 1, SK.stoneHi); px(g, x, y + 53, w, 4, alpha(K.night, 0.5));
} });
registerRoof('sanctRidgeWall', { parapet: false, paint: (g, x, y, w, h) => {
  canopy(g, x, y, w, h, 'sanct');
  // the ridge: a dry-stone retaining wall with a Kanemori sensor fence along its top
  const t = y + 14;
  px(g, x, t, w, 36, K.ink);
  for (let j = 0; j < 34; j += 9) for (let i = (j / 9) % 2 ? -10 : 0; i < w; i += 20) {
    const v = hash2(x + i, j + y), sw = 18, c = v < 0.3 ? SK.stoneHi : v < 0.75 ? SK.stone : SK.stoneLo;
    px(g, x + i + 1, t + j + 1, sw, 8, c); px(g, x + i + 1, t + j + 1, sw, 1, lt(c, 0.2));
  }
  for (let i = x + 8; i < x + w; i += 48) { px(g, i, t - 18, 4, 20, K.ink); px(g, i + 1, t - 18, 2, 19, K.metalHi); px(g, i, t - 20, 4, 3, K.cyan); }
  for (const yy of [t - 14, t - 8]) px(g, x, yy, w, 1, alpha(K.cyan, 0.55));
  px(g, x, t + 36, w, 4, alpha(K.night, 0.5));
} });
registerRoof('monaHallRoof', { parapet: false, paint: (g, x, y, w, h, seed) => {
  canopy(g, x, y, w, h, 'mona');
  hipRoof(g, x + 10, y + 30, w - 20, h - 30, 0.36, Math.round((w - 20) * 0.26), OLD_TILE);
  ruinRoof(g, x + 10, y + 30, w - 20, h - 30, Math.floor(seed), 4);
  // the south-east corner has caved in: a slump of tiles and timber
  const cx = x + w - 150, cy = y + h - 120;
  px(g, cx, cy, 130, 108, K.night); for (let k = 0; k < 40; k++) { const a = hash2(k, 7), b = hash2(7, k); px(g, cx + Math.floor(a * 120), cy + Math.floor(b * 100), 10, 4, k % 3 ? OLD_TILE.s : SK.ash); }
  for (let k = 0; k < 6; k++) { const by = cy + 10 + k * 16; px(g, cx + 4, by, 120, 4, K.ink); px(g, cx + 4, by, 120, 2, SK.ash); }
  frame(g, cx, cy, 130, 108, K.ink, 2);
} });
registerRoof('monaPagodaRoof', { parapet: false, paint: (g, x, y, w, h) => {
  canopy(g, x, y, w, h, 'mona');
  const s = Math.min(w - 8, h - 8), px0 = x + (w - s) / 2, py0 = y + h - s - 4;
  hipRoof(g, px0, py0, s, s, 0.5, s / 2, OLD_TILE);
  // the upper tiers are gone: an open square of charred beams where they fell in
  const i = Math.round(s * 0.22), q = s - 2 * i;
  px(g, px0 + i, py0 + i, q, q, K.ink); px(g, px0 + i + 3, py0 + i + 3, q - 6, q - 6, K.night);
  for (let k = 0; k < 5; k++) { const o = 6 + k * Math.floor((q - 12) / 4); px(g, px0 + i + o, py0 + i, 4, q, SK.ashLo); px(g, px0 + i, py0 + i + o, q, 4, SK.ashLo); }
  for (let k = 0; k < q; k += 2) px(g, px0 + i + k, py0 + i + Math.round(k * 0.8), 3, 3, SK.ash); // a fallen post across it
  ruinRoof(g, px0, py0, s, s, 41, 1);
  frame(g, px0 + i, py0 + i, q, q, OLD_TILE.ridge, 2);
} });
registerRoof('monaShedRoof', { parapet: false, paint: (g, x, y, w, h) => {
  canopy(g, x, y, w, h, 'mona');
  hipRoof(g, x + 6, y + 8, w - 12, h - 8, 0.45, 40, OLD_TILE);
  ruinRoof(g, x + 6, y + 8, w - 12, h - 8, 13, 2);
  for (let k = 0; k < w - 30; k += 2) px(g, x + 14 + k, y + 30 + Math.round(Math.sin(k / 40) * 6), 2, 2, K.ink); // the crack where it sags
} });
registerRoof('monaRuinWall', { parapet: false, paint: (g, x, y, w, h) => {
  canopy(g, x, y, w, h, 'mona');
  // a crumbled tsuiji wall: broken cap, plaster fallen off to the mud core, gaps choked with fern
  for (let i = 0; i < w; i += 16) {
    const v = hash2(x + i, 3), gone = v < 0.18; if (gone) { speckle(g, x + i, y + 14, 16, 34, SK.moss, 0.4, i); continue; }
    const top = y + 4 + Math.floor(v * 8);
    px(g, x + i, top, 16, 10, OLD_TILE.s); px(g, x + i, top, 16, 1, OLD_TILE.ridgeHi); for (let k = 2; k < 16; k += 5) px(g, x + i + k, top + 1, 1, 8, OLD_TILE.line);
    px(g, x + i, top + 10, 16, 2, K.ink);
    px(g, x + i, top + 12, 16, 46 - (top - y) - 12, v < 0.5 ? mix(SK.paper, SK.stoneLo, 0.45) : mix(K.wood, K.slate, 0.5));
    speckle(g, x + i, top + 12, 16, 30, SK.mossLo, 0.12, i + 1);
  }
  px(g, x, y + 46, w, 6, SK.stoneLo); speckle(g, x, y + 46, w, 6, SK.moss, 0.3, 9); px(g, x, y + 52, w, 4, alpha(K.night, 0.5));
} });

// ================================================================== fronts (x, y: top-left of the south wall)
registerFront('sanctFill', {}, () => { /* forest and mountain filler: face 0, never painted */ });

/** eave shadow with rafter ends lit by the lanterns */
function eave(g: G, x: number, y: number, w: number, wood: string) {
  px(g, x, y, w, 14, K.ink); px(g, x, y + 14, w, 4, alpha(K.night, 0.5));
  for (let i = x + 3; i < x + w - 3; i += 8) { px(g, i, y + 4, 5, 5, wood); px(g, i, y + 4, 5, 1, lt(wood, 0.3)); }
}
/** a lattice of shoji paper lit from inside */
function shoji(g: G, x: number, y: number, w: number, h: number, lit: number, L: LightFn) {
  px(g, x - 2, y - 2, w + 4, h + 4, K.ink);
  px(g, x, y, w, h, mix(SK.paper, K.warmHi, lit * 0.6)); if (lit > 0.3) px(g, x, y + h - 10, w, 10, mix(K.warmHi, K.warm, 0.3));
  for (let i = x + 7; i < x + w; i += 8) px(g, i, y, 1, h, K.wood);
  for (let j = y + 9; j < y + h; j += 10) px(g, x, j, w, 1, K.wood);
  if (lit > 0.3) L(x, y, w, h, K.amber, 0.3 + lit * 0.3, false);
}
function pillar(g: G, x: number, y: number, h: number, c: string, hi: string, lo: string) {
  px(g, x - 1, y, 12, h, K.ink); px(g, x, y, 10, h, c); px(g, x, y, 3, h, hi); px(g, x + 7, y, 3, h, lo);
  px(g, x - 2, y + h - 6, 14, 6, K.ink); px(g, x - 1, y + h - 5, 12, 4, SK.stone); // stone footing
}

registerFront('sanctHall', { wall: SK.ashLo, upper: 'none' }, ({ g, x, y, w, h, L }) => {
  px(g, x, y, w, h, dk(K.wood, 0.3));
  eave(g, x, y, w, K.woodHi);
  px(g, x, y + 18, w, 3, K.cyan); px(g, x, y + 21, w, 1, alpha(K.cyan, 0.35)); // the corp's LED line under the eave
  L(x, y + 16, w, 6, K.cyan, 0.3);
  const top = y + 22, deck = y + h - 26, bays = 7, bw = Math.floor((w - 20) / bays);
  for (let k = 0; k < bays; k++) {
    const bx = x + 10 + k * bw, inner = k >= 2 && k <= 4;
    if (inner) shoji(g, bx + 14, top + 6, bw - 18, deck - top - 10, k === 3 ? 1 : 0.75, L);
    else { // plaster panels with rails
      px(g, bx + 12, top + 4, bw - 14, deck - top - 6, SK.paper); speckle(g, bx + 12, top + 4, bw - 14, deck - top - 6, K.plaster, 0.04, k);
      for (const j of [top + 4, top + 26, deck - 6]) { px(g, bx + 12, j, bw - 14, 4, K.wood); px(g, bx + 12, j, bw - 14, 1, K.woodHi); }
    }
  }
  for (let k = 0; k <= bays; k++) pillar(g, x + 2 + k * bw, top, deck - top, SK.verm, SK.vermHi, SK.vermLo);
  // gilded name plaque under the eave
  const cx = x + w / 2; px(g, cx - 34, top - 2, 68, 22, K.ink); px(g, cx - 32, top, 64, 18, K.wood); frame(g, cx - 31, top + 1, 62, 16, SK.gold);
  for (let i = 0; i < 4; i++) { px(g, cx - 24 + i * 13, top + 5, 8, 2, SK.goldHi); px(g, cx - 21 + i * 13, top + 5, 2, 9, SK.goldHi); px(g, cx - 24 + i * 13, top + 11, 8, 2, SK.goldHi); }
  // the veranda and the wide stair down to the gravel
  px(g, x, deck, w, 8, K.ink); px(g, x + 2, deck + 1, w - 4, 6, K.woodHi); for (let i = x + 4; i < x + w; i += 12) px(g, i, deck + 1, 1, 6, K.wood);
  px(g, x, deck + 8, w, 18, SK.stoneLo); for (let i = x; i < x + w; i += 24) px(g, i, deck + 8, 1, 18, SK.stoneDk); px(g, x, deck + 8, w, 1, SK.stoneHi);
  const sw = 140; for (let s = 0; s < 4; s++) { const sy = deck + 2 + s * 6; px(g, cx - sw / 2 - s * 4, sy, sw + s * 8, 6, s % 2 ? SK.stone : SK.stoneHi); px(g, cx - sw / 2 - s * 4, sy + 5, sw + s * 8, 1, SK.stoneDk); }
  // offering box and the big paper lantern over the stair
  px(g, cx - 22, deck - 14, 44, 14, K.ink); px(g, cx - 21, deck - 13, 42, 12, K.wood); for (let i = cx - 19; i < cx + 20; i += 4) px(g, i, deck - 13, 2, 3, K.woodLo);
  disc(g, cx, top + 46, 14, 18, K.ink); disc(g, cx, top + 46, 12, 16, SK.verm); for (let j = -12; j <= 12; j += 4) px(g, cx - 11, top + 46 + j, 22, 1, SK.vermLo);
  px(g, cx - 10, top + 28, 20, 4, K.ink); px(g, cx - 10, top + 62, 20, 4, K.ink); px(g, cx - 3, top + 38, 6, 16, K.ink);
  L(cx - 14, top + 30, 28, 32, K.red, 0.55, true);
  for (const lx of [x + 10 + bw * 2 - 4, x + 10 + bw * 5 - 4]) lantern(g, lx, top + 6, K.warm, L);
});
registerFront('sanctPagoda', { wall: SK.ashLo, upper: 'none' }, ({ g, x, y, w, h, L }) => {
  px(g, x, y, w, h, dk(K.wood, 0.3)); eave(g, x, y, w, K.woodHi);
  const top = y + 18, base = y + h - 16;
  px(g, x + 16, top, w - 32, base - top, SK.paper);
  for (let k = 0; k < 4; k++) pillar(g, x + 16 + k * Math.floor((w - 42) / 3), top, base - top, SK.verm, SK.vermHi, SK.vermLo);
  const dx = x + w / 2 - 24; px(g, dx - 2, top + 8, 52, base - top - 8, K.ink); px(g, dx, top + 10, 48, base - top - 10, K.wood);
  for (let i = dx + 3; i < dx + 46; i += 6) px(g, i, top + 12, 3, base - top - 14, K.woodLo); px(g, dx + 22, top + 30, 4, 6, SK.gold);
  for (const sx of [x + 30, x + w - 54]) { px(g, sx, top + 12, 24, 22, K.ink); for (let i = 0; i < 24; i += 4) px(g, sx + i + 1, top + 13, 2, 20, SK.ashHi); } // slatted windows
  px(g, x + 4, base, w - 8, 16, SK.stone); px(g, x + 4, base, w - 8, 2, SK.stoneHi); for (let i = x + 8; i < x + w - 8; i += 22) px(g, i, base + 2, 1, 14, SK.stoneDk);
  lantern(g, x + 22, top + 4, K.warm, L); lantern(g, x + w - 30, top + 4, K.warm, L);
});
registerFront('sanctKura', { wall: SK.paper, upper: 'none' }, ({ g, x, y, w, h }) => {
  px(g, x, y, w, h, SK.paper); px(g, x, y, w, 10, K.ink); px(g, x, y + 10, w, 3, alpha(K.night, 0.4));
  const ny = y + h - 40; // namako wall: dark tiles with raised white diagonal joints
  px(g, x, ny, w, 40, dk(K.slate, 0.55));
  for (let j = 0; j < 40; j += 10) for (let i = 0; i < w; i += 10) { px(g, x + i + 4, ny + j + 1, 2, 8, SK.paper); px(g, x + i + 1, ny + j + 4, 8, 2, SK.paper); }
  px(g, x + w / 2 - 14, y + 20, 28, 22, K.ink); px(g, x + w / 2 - 12, y + 22, 24, 18, K.metal); px(g, x + w / 2 - 1, y + 22, 2, 18, K.ink); // iron shutters
  px(g, x, y + h - 2, w, 2, K.ink);
});
registerFront('sanctBell', { wall: SK.ashLo, upper: 'none' }, ({ g, x, y, w, h, L }) => {
  px(g, x, y, w, h, K.night); eave(g, x, y, w, K.woodHi);
  const base = y + h - 18;
  px(g, x + 2, base, w - 4, 18, SK.stone); px(g, x + 2, base, w - 4, 2, SK.stoneHi); for (let i = x + 8; i < x + w; i += 20) px(g, i, base + 2, 1, 16, SK.stoneDk);
  pillar(g, x + 6, y + 18, base - y - 18, SK.verm, SK.vermHi, SK.vermLo); pillar(g, x + w - 16, y + 18, base - y - 18, SK.verm, SK.vermHi, SK.vermLo);
  const cx = x + w / 2; // bronze bell: lip, bands, the boss studs, and the log striker on ropes
  px(g, cx - 1, y + 18, 3, 6, K.ink);
  px(g, cx - 18, y + 24, 36, 44, K.ink); px(g, cx - 16, y + 26, 32, 42, SK.bronze); px(g, cx - 16, y + 26, 8, 42, SK.bronzeHi);
  for (const j of [y + 36, y + 56]) px(g, cx - 16, j, 32, 2, dk(SK.bronze, 0.3));
  for (let j = 0; j < 3; j++) for (let i = 0; i < 3; i++) px(g, cx - 2 + i * 5, y + 28 + j * 4, 2, 2, SK.verdigris);
  px(g, cx - 20, y + 64, 40, 5, K.ink); px(g, cx - 19, y + 65, 38, 3, SK.bronzeHi);
  px(g, x + 18, y + 40, 4, 1, K.ink); px(g, x + 14, y + 41, 6, 6, K.ink); px(g, x + 15, y + 42, 22, 4, K.wood); px(g, x + 15, y + 42, 22, 1, K.woodHi);
  lantern(g, x + w - 26, y + 22, K.warm, L);
});
registerFront('sanctBooth', { wall: K.concrete, upper: 'none' }, ({ g, x, y, w, h, L }) => {
  px(g, x, y, w, h, K.concrete); speckle(g, x, y, w, h, K.concreteLo, 0.05, 5);
  px(g, x, y, w, 6, K.concreteHi); px(g, x, y + 6, w, 3, K.cyan); px(g, x, y + 9, w, 1, alpha(K.cyan, 0.4));
  // the guard room: a long lit window with two guards at their screens
  const wy = y + 16, wh = 40, wx = x + 16, ww = w - 120;
  px(g, wx - 2, wy - 2, ww + 4, wh + 4, K.ink); px(g, wx, wy, ww, wh, dk(K.cyan, 0.55)); px(g, wx, wy, ww, 3, K.glassHi);
  for (let i = wx + 50; i < wx + ww; i += 60) px(g, i, wy, 2, wh, K.ink);
  for (const gx of [wx + 40, wx + 130]) { disc(g, gx, wy + 20, 5, 5, K.ink); px(g, gx - 8, wy + 24, 16, 16, K.ink); px(g, gx + 10, wy + 26, 18, 10, K.ink); px(g, gx + 11, wy + 27, 16, 8, lt(K.cyan, 0.3)); }
  L(wx, wy, ww, wh, K.cyan, 0.5, true);
  const s = 'KANEMORI', tw = textW(s, 2); text(g, s, wx + ww / 2 - tw / 2, wy + 4, lt(K.cyan, 0.6), 2);
  // steel door with a card reader, and a stencilled checkpoint number
  const dx = x + w - 90; px(g, dx - 2, y + 18, 40, h - 22, K.ink); px(g, dx, y + 20, 36, h - 24, K.metal); px(g, dx, y + 20, 36, 2, K.metalHi);
  px(g, dx + 40, y + 40, 8, 12, K.ink); px(g, dx + 42, y + 42, 4, 3, K.cyan); L(dx + 38, y + 38, 12, 14, K.cyan, 0.35);
  text(g, 'K 01', x + w - 44, y + 22, K.ink, 2);
  for (let i = x; i < x + w; i += 16) px(g, i, y + h - 10, 8, 6, (i / 16) % 2 ? K.warm : K.ink); // hazard kick
  px(g, x, y + h - 4, w, 4, K.ink);
});

// ---- monastery
registerFront('monaHall', { wall: SK.ashLo, upper: 'none' }, ({ g, x, y, w, h, L, seed }) => {
  px(g, x, y, w, h, K.night); eave(g, x, y, w, SK.ash);
  const top = y + 18, deck = y + h - 24, bays = 6, bw = Math.floor((w - 20) / bays);
  for (let k = 0; k < bays; k++) {
    const bx = x + 10 + k * bw, v = hash2(k, seed);
    if (k === 2 || k === 3) { // the doors: one torn off and dark, one with a single candle burning inside
      px(g, bx + 12, top + 8, bw - 16, deck - top - 10, K.ink);
      if (k === 3) { disc(g, bx + bw / 2, deck - 20, 6, 8, alpha(K.amber, 0.25)); px(g, bx + bw / 2 - 1, deck - 24, 2, 8, K.warmHi); L(bx + bw / 2 - 10, deck - 30, 20, 20, K.amber, 0.35); }
      for (let i = bx + 14; i < bx + bw - 6; i += 8) if (hash2(i, k) < 0.5) px(g, i, top + 8, 1, 20 + Math.floor(hash2(k, i) * 20), SK.ashLo); // broken lattice
    } else {
      px(g, bx + 12, top + 4, bw - 14, deck - top - 6, mix(SK.paper, SK.stoneLo, 0.55)); speckle(g, bx + 12, top + 4, bw - 14, deck - top - 6, SK.stoneDk, 0.06, k);
      if (v < 0.5) { const hy = top + 20 + Math.floor(v * 20); px(g, bx + 20, hy, 26, 22, K.ink); px(g, bx + 22, hy + 2, 22, 18, K.night); } // holes in the plaster
      for (const j of [top + 4, deck - 6]) { px(g, bx + 12, j, bw - 14, 4, SK.ash); }
    }
  }
  for (let k = 0; k <= bays; k++) { // pillars: the vermilion mostly flaked off to grey wood
    const pxx = x + 2 + k * bw; pillar(g, pxx, top, deck - top, SK.ash, SK.ashHi, SK.ashLo);
    for (let j = top + 4; j < deck; j += 6) if (hash2(k, j) < 0.35) px(g, pxx + 1, j, 8, 3, SK.vermLo);
  }
  // veranda with planks missing, the stair cracked and sunk, moss and fern along the footing
  px(g, x, deck, w, 8, K.ink); for (let i = x + 2; i < x + w - 2; i += 12) if (hash2(i, 3) > 0.25) { px(g, i, deck + 1, 11, 6, SK.ash); px(g, i, deck + 1, 11, 1, SK.ashHi); }
  px(g, x, deck + 8, w, 16, SK.stoneLo); speckle(g, x, deck + 8, w, 16, SK.moss, 0.25, 4); speckle(g, x, deck + 8, w, 16, SK.mossHi, 0.06, 5);
  const cx = x + w / 2, sw = 120; for (let s = 0; s < 3; s++) { const sy = deck + 4 + s * 7; px(g, cx - sw / 2 - s * 4, sy, sw + s * 8, 6, s % 2 ? SK.stoneLo : SK.stone); }
  for (let k = 0; k < 5; k++) { const a = cx - 50 + k * 22; px(g, a, deck + 5 + (k % 2) * 7, 12, 1, K.ink); } // cracks
  // vines hanging off the eave
  for (let i = x + 6; i < x + w; i += 14) { const l = 10 + Math.floor(hash2(i, 9) * 40); for (let j = 0; j < l; j += 2) px(g, i + (j % 6 === 0 ? 1 : 0), y + 14 + j, 2, 2, j % 4 ? SK.moss : SK.mossLo); }
  px(g, cx - 30, top - 1, 60, 16, K.ink); px(g, cx - 28, top + 1, 56, 12, SK.ashLo); px(g, cx - 2, top + 1, 6, 12, K.ink); // the split plaque
});
registerFront('monaPagoda', { wall: SK.ashLo, upper: 'none' }, ({ g, x, y, w, h }) => {
  px(g, x, y, w, h, K.night); eave(g, x, y, w, SK.ash);
  const top = y + 18, base = y + h - 18;
  px(g, x + 16, top, w - 32, base - top, mix(SK.paper, SK.stoneLo, 0.6)); speckle(g, x + 16, top, w - 32, base - top, SK.stoneDk, 0.08, 3);
  px(g, x + 60, top + 10, 70, base - top - 10, K.ink); px(g, x + 64, top + 14, 62, base - top - 14, K.night); // the wall has burst open
  for (let k = 0; k < 4; k++) { const pxx = x + 16 + k * Math.floor((w - 42) / 3); pillar(g, pxx, top, base - top, SK.ash, SK.ashHi, SK.ashLo); }
  for (let k = 0; k < 30; k += 2) px(g, x + 70 + k * 2, top + 10 + k, 6, 4, SK.ash); // a fallen beam leaning through the gap
  px(g, x + 4, base, w - 8, 18, SK.stoneLo); speckle(g, x + 4, base, w - 8, 18, SK.moss, 0.3, 6);
  for (let i = 0; i < 6; i++) px(g, x + 10 + i * 40, base - 6 + (i % 2) * 3, 20, 8, i % 2 ? SK.stone : OLD_TILE.s); // rubble on the footing
});
registerFront('monaShed', { wall: SK.ashLo, upper: 'none' }, ({ g, x, y, w, h }) => {
  px(g, x, y, w, h, K.night); eave(g, x, y, w, SK.ash);
  const base = y + h - 12;
  px(g, x, base, w, 12, SK.stoneLo); speckle(g, x, base, w, 12, SK.moss, 0.3, 2);
  for (const [px0, lean] of [[x + 10, 0], [x + w - 20, -6]] as const) for (let j = y + 14; j < base; j++) px(g, px0 + Math.round(lean * (j - y) / h), j, 8, 1, SK.ash);
  // the bell fell: it lies on its side, cracked, gone green
  const cx = x + w / 2, cy = base - 10;
  disc(g, cx, cy, 30, 12, K.ink); disc(g, cx, cy, 28, 10, SK.verdigris); px(g, cx - 28, cy - 4, 56, 2, mix(SK.verdigris, K.white, 0.2));
  disc(g, cx + 24, cy, 6, 10, K.ink); disc(g, cx + 24, cy, 4, 8, SK.bronze);
  for (let k = 0; k < 14; k++) px(g, cx - 10 + k, cy - 8 + k, 1, 1, K.ink);
});
